import React, { useState } from 'react';

interface ConfirmStepProps {
  did: string;
  didDocument: any;
  onConfirm: (name: string, description: string) => void;
  onBack: () => void;
  onCancel: () => void;
}

const ConfirmStep: React.FC<ConfirmStepProps> = ({ 
  did, 
  didDocument, 
  onConfirm, 
  onBack, 
  onCancel 
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Please enter a name for the trust registry');
      return;
    }

    onConfirm(name.trim(), description.trim());
  };

  return (
    <div className="card shadow">
      <div className="card-body">
        <h4 className="mb-3">
          <i className="fas fa-cog mr-2"></i>
          Configure Trust Registry
        </h4>

        <p className="text-muted mb-4">
          The DID has been successfully resolved and validated. Please provide a name and description for this trust registry.
        </p>

        <div className="alert alert-info mb-4">
          <h6 className="font-weight-bold mb-2">
            <i className="fas fa-info-circle mr-2"></i>
            Resolved DID Document
          </h6>
          <dl className="row mb-0">
            <dt className="col-sm-3">DID:</dt>
            <dd className="col-sm-9">
              <code style={{ fontSize: '0.85rem' }}>{didDocument.id}</code>
            </dd>

            {didDocument.verificationMethod && didDocument.verificationMethod.length > 0 && (
              <>
                <dt className="col-sm-3">Verification Methods:</dt>
                <dd className="col-sm-9">
                  {didDocument.verificationMethod.length} method(s)
                </dd>
              </>
            )}

            {didDocument.service && didDocument.service.length > 0 && (
              <>
                <dt className="col-sm-3">Services:</dt>
                <dd className="col-sm-9">
                  {didDocument.service.length} service(s)
                  <ul className="mt-2 mb-0">
                    {didDocument.service.map((service: any, idx: number) => (
                      <li key={idx}>
                        <small>
                          {Array.isArray(service.type) ? service.type.join(', ') : service.type}:
                          {' '}
                          <code style={{ fontSize: '0.75rem' }}>
                            {typeof service.serviceEndpoint === 'string' 
                              ? service.serviceEndpoint 
                              : JSON.stringify(service.serviceEndpoint)}
                          </code>
                        </small>
                      </li>
                    ))}
                  </ul>
                </dd>
              </>
            )}
          </dl>

          <details className="mt-3">
            <summary className="cursor-pointer text-primary" style={{ cursor: 'pointer' }}>
              <small>View full DID document</small>
            </summary>
            <pre className="mt-2 p-2 bg-white border rounded" style={{ fontSize: '0.75rem', maxHeight: '300px', overflow: 'auto' }}>
              {JSON.stringify(didDocument, null, 2)}
            </pre>
          </details>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="name" className="form-label">
              <strong>Trust Registry Name *</strong>
            </label>
            <input
              type="text"
              className={`form-control ${error && !name.trim() ? 'is-invalid' : ''}`}
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter a friendly name for this trust registry"
              required
            />
            {error && !name.trim() && (
              <div className="invalid-feedback">
                {error}
              </div>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="description" className="form-label">
              <strong>Description</strong>
            </label>
            <input
              className="form-control"
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter an optional description for this trust registry"
            />
          </div>

          <div className="d-flex justify-content-between">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onBack}
            >
              <i className="fas fa-arrow-left mr-1"></i> Back
            </button>
            <button
              type="submit"
              className="btn btn-success"
            >
              <i className="fas fa-check mr-1"></i> Create Trust Registry
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConfirmStep;
